import { Box, Group, LoadingOverlay, Space, Text } from "@mantine/core";
import CustomTable from "../../components/tables/CustomTable";
import { MeterCutoffsDataType } from "../../types";
import { getPreviousMonthYear, formatDate } from "../../utils/date";
import TablePagination from "../../components/pagination";
import { formatNumberWithCommas } from "../../utils/numbers";

type MonthlyCutoffsTableProps = MeterCutoffsDataType["monthlyCutoffs"] & {
  onPageChange: (page: number) => void;
  onLimitSelect: (limit: string | null) => void;
  showLiters: boolean;
};

function formatVolume(value: number, showLiters: boolean) {
  if (!(value >= 0)) return <Text size="sm">--</Text>;
  if (showLiters) return <Text size="sm">{formatNumberWithCommas(value)}</Text>;
  const truncated = Math.trunc(value / 10) / 100;
  const [whole, dec] = truncated.toFixed(2).split(".");
  return (
    <Group gap={0}>
      <Text size="sm" fw={700}>{formatNumberWithCommas(Number(whole))}</Text>
      <Text size="xs" fw={400} c="dimmed">.{dec}</Text>
    </Group>
  );
}

export default function MonthlyCutoffsTable({
  loading,
  results,
  error,
  limit,
  page,
  totalPages,
  onLimitSelect,
  onPageChange,
  showLiters,
}: MonthlyCutoffsTableProps) {
  const headers = [
    { label: "Period", accessor: "month" },
    { label: "Cut-off timestamp", accessor: "cutoffTimestamp" },
    { label: showLiters ? "Previous Month Rd. (l)" : "Previous Month Rd. (units)", accessor: "previousReading" },
    { label: showLiters ? "End Of Month Reading (l)" : "End Of Month Reading (units)", accessor: "endMonthReading" },
    { label: showLiters ? "Monthly Consumption (l)" : "Monthly Consumption (units)", accessor: "consumption" },
  ];

  const data = results?.map((data) => ({
    id: data?.id,
    month: <Text size="sm">{getPreviousMonthYear(data?.cutoffDateTime)}</Text>,
    cutoffTimestamp: <Text size="sm">{formatDate(data?.cutoffDateTime)}</Text>,
    previousReading: data?.previousReading !== null && data?.previousReading !== undefined
      ? formatVolume(data.previousReading, showLiters)
      : <Text size="sm" c="dimmed">--</Text>,
    endMonthReading: formatVolume(data?.reading, showLiters),
    consumption: formatVolume(data?.consumption, showLiters),
  }));

  return (
    <Box pos={"relative"} mih={"20vh"}>
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        overlayProps={{
          radius: "sm",
          blur: 0.5,
          backgroundOpacity: 0.5,
          color: "#edf2fb",
        }}
      />
      {error ? (
        <Text my={"md"} fw={600} size="lg" ta={"center"}>
          Failed to load data. <br /> Please contact admin.
        </Text>
      ) : data?.length ? (
        <>
          <CustomTable headers={headers} rows={data} />
          <Space h="sm" />
          <TablePagination
            currentPage={page}
            limit={limit}
            totalPages={totalPages}
            onLimitSelect={onLimitSelect}
            onPageChange={onPageChange}
          />
        </>
      ) : loading ? null : (
        <Text my={"md"} fw={600} size="lg" ta={"center"}>
          No data available.
        </Text>
      )}
    </Box>
  );
}
